import React from 'react';
import PropTypes from 'prop-types';
import { cn } from 'src/lib/utils';
import { daysSince, daysUntil } from './tabFormatters';

// "Waiting" column — senior_level_flow's Contacted tab only. Three pieces,
// all off the candidate-list API per explicit instruction:
//   - a day count, from row.contact_link_send_at (when the first contact
//     link went out) to today — "Sent today" instead of the confusing
//     "0 days" once that gap is actually zero
//   - a dot trail, one dot per real row.notifications.schedules entry — no
//     padding to a fixed count: a 1-entry array shows 1 dot, a 4-entry
//     array shows 4. A real entry's `sent` boolean colors its dot in.
//   - a "Next in Nd" line off the first not-yet-sent schedule entry (the
//     array is already in chronological order, so the first `sent: false`
//     entry is the next one coming up) — Nd is the whole-calendar-day gap
//     from today to that entry's own `schedule` date. Omitted once every
//     entry has already gone out (nothing left to look forward to).
//
// This is the same "day count + dot trail" visual language WaitingCell.jsx
// already built for the secondary flow's First Contact tab — kept as a
// separate component rather than merged into that one, since that one is
// still built on guessed field names (waiting_days/waiting_status_text, no
// confirmed contract for that flow — see its own file comment) and a fixed
// 3-band dot count, not a real per-candidate schedules array. Unifying them
// would mean forcing this tab's real data through that placeholder's shape,
// or the reverse; two small, honest components for two different flows'
// actual data beats one component straddling a confirmed and an unconfirmed
// contract.
//
// Color tiers on the day count — confirmed against the shared screenshot's
// own data points (7/5 days → red, 4/3 → orange, 2 → neutral) — also drive
// the dot trail's fill color, so a row's urgency reads consistently across
// both pieces rather than introducing a second, unrelated dot palette.
const RED_THRESHOLD_DAYS = 5;
const ORANGE_THRESHOLD_DAYS = 3;

const tierForDays = (days) => {
  if (days >= RED_THRESHOLD_DAYS) {
    return { textClass: 'text-ds-danger', dotColor: 'var(--ds-danger)' };
  }
  if (days >= ORANGE_THRESHOLD_DAYS) {
    return { textClass: 'text-ds-warning', dotColor: 'var(--ds-warning)' };
  }
  return { textClass: 'text-foreground-strong', dotColor: null };
};

const ScheduleDots = ({ schedules, dotColor }) => (
  <span className="inline-flex items-center gap-[3px]">
    {schedules.map((s, i) => (
      <span
        // eslint-disable-next-line react/no-array-index-key
        key={s.schedule || i}
        className={cn(
          'h-[6px] w-[6px] rounded-full',
          !s.sent && 'bg-[#E5E9EE]'
        )}
        style={
          s.sent
            ? { backgroundColor: dotColor || 'var(--ds-warning)' }
            : undefined
        }
        aria-hidden="true"
      />
    ))}
  </span>
);

ScheduleDots.propTypes = {
  schedules: PropTypes.arrayOf(
    PropTypes.shape({
      schedule: PropTypes.string,
      sent: PropTypes.bool,
    })
  ).isRequired,
  dotColor: PropTypes.string,
};

ScheduleDots.defaultProps = {
  dotColor: null,
};

const ContactWaitingCell = ({ row }) => {
  const days = daysSince(row.contact_link_send_at);
  const schedules = Array.isArray(row.notifications?.schedules)
    ? row.notifications.schedules
    : [];

  if (days == null && schedules.length === 0) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  const { textClass, dotColor } = tierForDays(days ?? 0);
  const nextSchedule = schedules.find((s) => !s.sent);
  const nextInDays = nextSchedule ? daysUntil(nextSchedule.schedule) : null;

  return (
    <div className="min-w-0">
      {days != null &&
        (days === 0 ? (
          <p className={cn('text-sm font-semibold', textClass)}>Sent today</p>
        ) : (
          <p className="text-sm">
            <span className={cn('font-semibold', textClass)}>{days}</span>{' '}
            <span className="text-xs text-muted-foreground">
              {days === 1 ? 'day' : 'days'}
            </span>
          </p>
        ))}
      {schedules.length > 0 && (
        <div className="mt-1 flex items-center justify-start gap-1.5">
          <ScheduleDots schedules={schedules} dotColor={dotColor} />
          {nextInDays != null && (
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              Next in {nextInDays}d
            </span>
          )}
        </div>
      )}
    </div>
  );
};

ContactWaitingCell.propTypes = {
  row: PropTypes.shape({
    contact_link_send_at: PropTypes.string,
    notifications: PropTypes.shape({
      schedules: PropTypes.arrayOf(
        PropTypes.shape({
          schedule: PropTypes.string,
          sent: PropTypes.bool,
        })
      ),
    }),
  }).isRequired,
};

export const contactWaitingColumn = {
  key: 'contact_waiting',
  header: 'Waiting',
  className: 'text-sm',
  // Left-aligned per explicit request — every senior_level_flow column
  // reads left-to-right like the Candidate column, for a consistent
  // layout across tabs. The dot trail + "Next in Nd" row is a flex
  // container (justify-start here, matching), so it doesn't fight the
  // cell's own text-align the way a justify-end row would.
  align: 'left',
  width: '150px',
  render: (_v, row) => <ContactWaitingCell row={row} />,
};

export default ContactWaitingCell;
